import { getClosest, makeXScale } from '@weenat/client/dist/core/charts'
import { DataPoint } from '@weenat/client/dist/core/charts/d3-charts.type'
import { isEmpty, isNil } from 'lodash-es'
import {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import useResizeObserver from 'use-resize-observer'
import { ChartProps } from './Chart'

export interface CursorContext {
  cursorPos: { x: number; y: number } | undefined
  activeDataPoint: DataPoint | undefined
  height: number | undefined
  marginBottom: number | undefined
  isCursorXBeforeNow: boolean
}

const cursorContext = createContext<CursorContext>({
  activeDataPoint: undefined,
  cursorPos: undefined,
  height: undefined,
  marginBottom: undefined,
  isCursorXBeforeNow: false
})

type CursorContextProps = Pick<
  ChartProps,
  'height' | 'width' | 'marginBottom' | 'marginLeft' | 'marginRight' | 'marginTop' | 'domainPadding'
> & {
  data: DataPoint[]
  setDate?: (date: Date | undefined) => void
  children?: ReactNode
  domainX: ChartProps['domain']['x']
  containerRef?: RefObject<HTMLDivElement | null>
}

export const CursorContext: FC<CursorContextProps> = ({
  children,
  width: forcedWidth,
  height: forcedHeight,
  data,
  setDate,
  domainX,
  marginBottom = 32,
  marginLeft = 48,
  marginRight = 16,
  marginTop = 16,
  domainPadding,
  containerRef
}) => {
  const ref = useRef<HTMLDivElement>(null)

  const [dimensions, setDimension] = useState<{
    width: number | undefined
    height: number | undefined
  }>({
    width: typeof forcedWidth === 'number' ? forcedWidth : undefined,
    height: typeof forcedHeight === 'number' ? forcedHeight : undefined
  })

  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | undefined>(undefined)

  const [activeDataPoint, setActiveDataPoint] = useState<DataPoint | undefined>(undefined)

  const { width, height } = dimensions

  const xScale = useMemo(() => {
    if (isNil(width)) return undefined
    return makeXScale({
      domain: domainX,
      marginLeft,
      width,
      marginRight,
      paddingStart: domainPadding?.left,
      paddingEnd: domainPadding?.right
    })
  }, [domainPadding?.left, domainPadding?.right, domainX, marginLeft, marginRight, width])

  const nowInPx = !isNil(xScale) ? xScale(new Date()) : undefined

  const isCursorXBeforeNow = !isNil(cursorPos) && !isNil(nowInPx) ? cursorPos.x <= nowInPx : false

  // Only consider points inside the visible X domain when resolving the cursor.
  // A point with a date outside `domainX` maps to a pixel outside the plotting
  // area, but `getClosest` has no bounds check and will happily snap to it when
  // the cursor is near the chart edge — which surfaced in APP-4732 as tooltips
  // showing years (2021/2022) from stale data bleeding through from upstream
  // series. Filter here to guarantee `activeDataPoint.date` is always in-domain.
  const filteredData = useMemo(() => {
    if (isNil(data)) return data
    const [startDate, endDate] = domainX
    const start = startDate.getTime()
    const end = endDate.getTime()
    return data.filter((d) => {
      const t = d.date.getTime()
      return t >= start && t <= end
    })
  }, [data, domainX])

  const stampsX = useMemo(
    () =>
      !isNil(xScale) && !isNil(filteredData)
        ? filteredData?.map((d) => xScale(d.date.getTime()))
        : undefined,
    [filteredData, xScale]
  )

  const setDateHandler = useCallback(
    (index: number | undefined) => {
      if (!isNil(index) && !isNil(filteredData) && !isNil(filteredData[index])) {
        const newDataPoint = filteredData[index]
        setDate?.(newDataPoint.date)
        setActiveDataPoint(newDataPoint)
      } else {
        setDate?.(undefined)
        setActiveDataPoint(undefined)
      }
    },
    [filteredData, setDate]
  )

  const clear = useCallback(() => {
    setDateHandler(undefined)
    setCursorPos(undefined)
  }, [setDateHandler])

  useImperativeHandle(containerRef, () => ref.current, [])

  useResizeObserver({
    ref,
    onResize: (size) => {
      setDimension(size)
    }
  })

  const value = useMemo(
    (): CursorContext => ({
      activeDataPoint,
      cursorPos,
      height,
      marginBottom,
      isCursorXBeforeNow
    }),
    [activeDataPoint, cursorPos, height, marginBottom, isCursorXBeforeNow]
  )

  return (
    <div
      onMouseMove={(e) => {
        if (
          e.nativeEvent.offsetX <= (marginLeft ?? 0) ||
          (!isNil(width) && e.nativeEvent.offsetX >= width - marginRight) ||
          e.nativeEvent.offsetY <= (marginTop ?? 0) ||
          e.nativeEvent.offsetY >= (height ?? 0) - marginBottom
        ) {
          clear()
        } else if (!isNil(xScale) && !isNil(filteredData) && !isNil(stampsX) && !isEmpty(stampsX)) {
          const closestX = getClosest(stampsX, e.nativeEvent.offsetX)
          const snappedX = xScale(filteredData[closestX.idx].date)

          setDateHandler(closestX.idx)
          setCursorPos({
            x: snappedX,
            y: e.nativeEvent.offsetY
          })
        }
      }}
      onBlur={() => {
        clear()
      }}
      ref={ref}
      style={{ width: forcedWidth }}
    >
      <cursorContext.Provider value={value}>{children}</cursorContext.Provider>
    </div>
  )
}

export const useCursorContext = () => {
  return useContext(cursorContext)
}
